import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskAPI, projectAPI, userAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import PunchInOutWidget from '../components/PunchInOutWidget';
import './ProjectManagerDashboard.css';

// Line Icons Component
const Icon = ({ name, size = 20 }) => {
  const icons = {
    briefcase: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
    checkCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
    dollarSign: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
    alertCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
    trendingUp: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>,
    activity: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
    target: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>,
    barChart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" /></svg>,
    calendar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
    eye: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
    plus: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
    layers: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>,
    layout: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>,
  };
  return icons[name] || null;
};

const ProjectManagerDashboard = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    totalTasks: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
    teamMembers: 0,
    budgetUtilization: 0
  });

  useEffect(() => {
    loadDashboardData();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    filterTasks(activeFilter);
  }, [tasks, activeFilter]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [tasksRes, projectsRes, usersRes] = await Promise.all([
        taskAPI.getAll(),
        projectAPI.getAll(),
        userAPI.getAll()
      ]);

      const taskData = tasksRes.data.data || [];
      const projectData = projectsRes.data.data || [];
      const userData = usersRes.data.data || [];

      setTasks(taskData);
      setProjects(projectData);
      setTeamMembers(userData);

      const now = new Date();
      const overdueTasks = taskData.filter(t =>
        t.dueDate && new Date(t.dueDate) < now && t.status !== 'Done'
      ).length;

      const activeProjects = projectData.filter(p => p.status === 'Active').length;

      setStats({
        totalProjects: projectData.length,
        activeProjects: activeProjects,
        totalTasks: taskData.length,
        inProgress: taskData.filter(t => t.status === 'In Progress').length,
        completed: taskData.filter(t => t.status === 'Done').length,
        overdue: overdueTasks,
        teamMembers: userData.length,
        budgetUtilization: 75
      });

      setError(null);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError('Unable to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  const filterTasks = (filter) => {
    if (filter === 'All') {
      setFilteredTasks(tasks);
    } else {
      setFilteredTasks(tasks.filter(t => t.status === filter));
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'done': return 'success';
      case 'in progress': return 'progress';
      case 'blocked': return 'blocked';
      default: return 'todo';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'high';
      case 'medium': return 'medium';
      default: return 'low';
    }
  };

  const getTaskTypeColor = (type) => {
    if (type === 'Bug Fix') return 'bug';
    if (type === 'Feature Development') return 'feature';
    return 'default';
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getProjectHealthColor = (project) => {
    if (!project) return '#6b7280';
    const progress = project.progress || 0;
    if (progress >= 80) return '#10b981';
    if (progress >= 50) return '#3b82f6';
    if (progress >= 30) return '#f59e0b';
    return '#ef4444';
  };

  if (loading && tasks.length === 0) {
    return (
      <Layout>
        <div className="pm-dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pm-dashboard">
        <div className="pm-header">
          <div className="pm-header-left">
            <h1 className="pm-title">Welcome back, {user?.name || 'Project Manager'}!</h1>
            <p className="pm-subtitle">{formatDate(currentTime)}</p>
          </div>
          <div className="pm-header-right">
            <div className="pm-clock">
              <Icon name="clock" size={18} />
              <span className="clock-time">{formatTime(currentTime)}</span>
            </div>
            <button className="btn-quick-action secondary" onClick={() => navigate('/projects')}>
              <Icon name="plus" size={16} />
              New Project
            </button>
            <button className="btn-quick-action" onClick={() => navigate('/tasks')}>
              <Icon name="checkCircle" size={16} />
              New Task
            </button>
          </div>
        </div>

        <PunchInOutWidget />

        <div className="pm-stats-grid">
          <div className="stat-card primary">
            <div className="stat-icon">
              <Icon name="briefcase" size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.totalProjects}</div>
              <div className="stat-label">Total Projects</div>
              <div className="stat-trend positive">↗ {stats.activeProjects} Active</div>
            </div>
          </div>

          <div className="stat-card success">
            <div className="stat-icon">
              <Icon name="checkCircle" size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.completed}</div>
              <div className="stat-label">Completed Tasks</div>
              <div className="stat-trend positive">↗ {Math.round((stats.completed / stats.totalTasks) * 100) || 0}%</div>
            </div>
          </div>

          <div className="stat-card warning">
            <div className="stat-icon">
              <Icon name="activity" size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.inProgress}</div>
              <div className="stat-label">In Progress</div>
              <div className="stat-trend neutral">→ Active</div>
            </div>
          </div>

          <div className="stat-card danger">
            <div className="stat-icon">
              <Icon name="clock" size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.overdue}</div>
              <div className="stat-label">Overdue Tasks</div>
              <div className="stat-trend negative">⚠ Urgent</div>
            </div>
          </div>

          <div className="stat-card info">
            <div className="stat-icon">
              <Icon name="users" size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.teamMembers}</div>
              <div className="stat-label">Team Members</div>
              <div className="stat-trend positive">→ Active</div>
            </div>
          </div>

          <div className="stat-card secondary">
            <div className="stat-icon">
              <Icon name="dollarSign" size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.budgetUtilization}%</div>
              <div className="stat-label">Budget Used</div>
              <div className="stat-trend neutral">→ On Track</div>
            </div>
          </div>
        </div>

        <div className="pm-content-grid">
          <div className="pm-main-content">
            <div className="pm-section">
              <div className="pm-section-header">
                <div>
                  <h2 className="section-title">
                    <Icon name="checkCircle" size={20} />
                    Project Tasks
                  </h2>
                  <p className="section-subtitle">{filteredTasks.length} tasks matching</p>
                </div>
                <div className="pm-filters">
                  <button
                    className={`pm-filter-btn ${activeFilter === 'All' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('All')}
                  >
                    All
                  </button>
                  <button
                    className={`pm-filter-btn ${activeFilter === 'To Do' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('To Do')}
                  >
                    To Do
                  </button>
                  <button
                    className={`pm-filter-btn ${activeFilter === 'In Progress' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('In Progress')}
                  >
                    In Progress
                  </button>
                  <button
                    className={`pm-filter-btn ${activeFilter === 'Done' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('Done')}
                  >
                    Done
                  </button>
                </div>
              </div>

              <div className="pm-tasks-grid">
                {filteredTasks.length === 0 ? (
                  <div className="pm-empty">
                    <Icon name="activity" size={48} />
                    <p>No tasks found for this filter</p>
                  </div>
                ) : (
                  filteredTasks.slice(0, 6).map(task => (
                    <div key={task._id} className="pm-task-card" onClick={() => navigate('/tasks')}>
                      <div className="task-card-header">
                        <h3 className="task-card-title">{task.title}</h3>
                        <span className={`pm-badge status-${getStatusColor(task.status)}`}>
                          {task.status || 'To Do'}
                        </span>
                      </div>
                      <p className="task-card-desc">{task.description?.substring(0, 80) || 'No description provided.'}...</p>
                      <div className="task-card-meta">
                        <span className={`pm-tag type-${getTaskTypeColor(task.type)}`}>
                          {task.type || 'Task'}
                        </span>
                        <span className={`pm-tag priority-${getPriorityColor(task.priority)}`}>
                          {task.priority || 'Low'}
                        </span>
                        <span className="task-card-hours">
                          <Icon name="clock" size={14} /> {task.estimatedHours || 0}h
                        </span>
                      </div>
                      {task.project && (
                        <div className="task-card-footer">
                          <span className="task-project-name">
                            <Icon name="briefcase" size={14} />
                            {task.project.name}
                          </span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="pm-section">
              <div className="pm-section-header">
                <div>
                  <h2 className="section-title">
                    <Icon name="layers" size={20} />
                    Active Projects
                  </h2>
                  <p className="section-subtitle">Overview of current project progress</p>
                </div>
                <button className="btn-view-all" onClick={() => navigate('/projects')}>View All</button>
              </div>
              <div className="pm-projects-list">
                {projects.filter(p => p.status === 'Active').length === 0 ? (
                  <div className="pm-empty">
                    <p>No active projects found</p>
                  </div>
                ) : (
                  projects.filter(p => p.status === 'Active').slice(0, 4).map(project => (
                    <div key={project._id} className="pm-project-card" onClick={() => navigate('/projects')}>
                      <div className="project-card-header">
                        <h3 className="project-card-title">{project.name}</h3>
                        <span className="pm-badge" style={{
                          backgroundColor: `${getProjectHealthColor(project)}20`,
                          color: getProjectHealthColor(project)
                        }}>
                          {project.status}
                        </span>
                      </div>
                      <div className="project-progress">
                        <div className="progress-bar">
                          <div className="progress-fill" style={{
                            width: `${project.progress || 0}%`,
                            backgroundColor: getProjectHealthColor(project)
                          }}></div>
                        </div>
                        <span className="progress-text">{project.progress || 0}%</span>
                      </div>
                      <div className="project-card-footer">
                        <span className="project-client">
                          <Icon name="users" size={14} />
                          {project.client?.name || 'No Client'}
                        </span>
                        <span className="project-deadline">
                          <Icon name="calendar" size={14} />
                          {project.endDate ? new Date(project.endDate).toLocaleDateString() : 'No dealine'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="pm-sidebar">
            <div className="pm-widget">
              <h3 className="widget-title">
                <Icon name="barChart" size={18} />
                Quick Stats
              </h3>
              <div className="quick-stats">
                <div className="quick-stat-item">
                  <div className="quick-stat-icon">
                    <Icon name="checkCircle" size={20} />
                  </div>
                  <div>
                    <div className="quick-stat-value">{stats.totalTasks}</div>
                    <div className="quick-stat-label">Total Tasks</div>
                  </div>
                </div>
                <div className="quick-stat-item">
                  <div className="quick-stat-icon">
                    <Icon name="target" size={20} />
                  </div>
                  <div>
                    <div className="quick-stat-value">{stats.activeProjects}</div>
                    <div className="quick-stat-label">Active Projects</div>
                  </div>
                </div>
                <div className="quick-stat-item">
                  <div className="quick-stat-icon">
                    <Icon name="alertCircle" size={20} />
                  </div>
                  <div>
                    <div className="quick-stat-value">{stats.overdue}</div>
                    <div className="quick-stat-label">Needs Attention</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pm-widget">
              <h3 className="widget-title">
                <Icon name="layout" size={18} />
                Quick Actions
              </h3>
              <div className="quick-actions">
                <button className="quick-action-btn" onClick={() => navigate('/projects')}>
                  <Icon name="briefcase" size={16} />
                  Manage Projects
                </button>
                <button className="quick-action-btn" onClick={() => navigate('/tasks')}>
                  <Icon name="checkCircle" size={16} />
                  View All Tasks
                </button>
                <button className="quick-action-btn" onClick={() => navigate('/resources')}>
                  <Icon name="users" size={16} />
                  Resource Planning
                </button>
                <button className="quick-action-btn" onClick={() => navigate('/gantt')}>
                  <Icon name="activity" size={16} />
                  Gantt Chart
                </button>
                <button className="quick-action-btn" onClick={() => navigate('/clients')}>
                  <Icon name="users" size={16} />
                  Client Management
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProjectManagerDashboard;
